import { useTheme } from "@mui/material/styles";
import Drawer from "@mui/material/Drawer";
import Container from "@mui/material/Container";
import { useCallback, useMemo } from "react";
import {
  createSearchParams,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { BASE_RESIDENTS_PATH, RESIDENTS_ROUTES } from "@routing/Residents";

import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@covertree/ui/buttons/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import { CoverageDetails } from "@routing/Residents/components/CoverageDetails/CoverageDetails";

import { DisplayPropertiesTable } from "@covertree/ui/content";
import { mapEventType } from "@utils/mapEventType/mapEventType";
import { useGetResidentQuery } from "@redux/residents/residentsApi";
import { skipToken } from "@reduxjs/toolkit/query";
import { Button } from "@covertree/ui";

export function CoverageEventDetails() {
  const theme = useTheme();
  const navigate = useNavigate();

  const { residentId: id, coverageId, eventId } = useParams();
  const [searchParams] = useSearchParams();

  const { currentData: resident, isError } = useGetResidentQuery(
    id ? { id } : skipToken,
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const { coverageBasedOnEvent, eventType } = useMemo(() => {
    const coverage = resident.coverages.find(({ id }) => id === coverageId);
    if (!coverage) {
      return {
        coverageBasedOnEvent: undefined,
        eventType: undefined,
      };
    }

    const event = coverage?.events?.find((e) => e.id === eventId);

    if (!event)
      return {
        coverageBasedOnEvent: undefined,
        eventType: undefined,
      };

    return {
      coverageBasedOnEvent: {
        ...event.eventData,
        id: coverage.id,
        policyType: coverage.policyType,
      },
      eventType: event.type,
    };
  }, [resident, eventId]);

  const onCloseDetailedPath = useMemo(
    () =>
      id
        ? `/${BASE_RESIDENTS_PATH}/${RESIDENTS_ROUTES.RESIDENT}/${encodeURIComponent(id)}`
        : `/${BASE_RESIDENTS_PATH}`,
    [id]
  );

  const onClose = useCallback(() => {
    navigate({
      pathname: onCloseDetailedPath,
      search: createSearchParams(searchParams).toString(),
    });
  }, [navigate, onCloseDetailedPath]);

  return (
    <Drawer
      anchor="bottom"
      open
      onClose={onClose}
      PaperProps={{
        sx: { borderRadius: theme.spacing(3, 3, 0, 0) },
      }}>
      <Container sx={{ minWidth: "100%", height: "90vh" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(15, 1fr)",
          }}>
          <Box
            justifyContent="space-between"
            display="flex"
            alignItems="center"
            position="sticky"
            top={0}
            gap={theme.spacing(2)}
            sx={{
              gridColumnStart: 2,
              gridColumnEnd: "span 12",
              backgroundColor: "background.default",
              zIndex: 100,
            }}>
            <Typography variant="h3">Policy Event Details</Typography>
          </Box>

          <Box
            justifyContent="flex-end"
            display="flex"
            alignItems="center"
            p={theme.spacing(4)}
            position="sticky"
            top={0}
            gap={theme.spacing(2)}
            sx={{ gridColumnEnd: "15" }}>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              gridColumnStart: 2,
              gridColumnEnd: "span 3",
              zIndex: 100,
            }}
            display="flex"
            flexDirection="column">
            <Box position="fixed">
              <Box display="flex" flexDirection="column">
                <Typography
                  p={theme.spacing(0.5, 0)}
                  sx={{
                    "&.active, &:hover": {
                      textDecoration: "none",
                    },
                  }}
                  color="primary">
                  Event Summary
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              gridColumnStart: 5,
              gridColumnEnd: "span 8",
              marginBottom: theme.spacing(3),
            }}>
            {isError || !coverageBasedOnEvent ? (
              <>
                <Typography lineHeight="400%">
                  Event not found. Contact us or try again later.
                </Typography>
              </>
            ) : (
              <CoverageDetails coverage={coverageBasedOnEvent}>
                <DisplayPropertiesTable
                  containerStyles={{
                    padding: theme.spacing(3),
                    width: "100%",
                  }}
                  properties={[
                    {
                      label: "Event Type",
                      value: mapEventType(eventType),
                    },
                  ]}
                />
              </CoverageDetails>
            )}
          </Box>

          <Box
            sx={{
              gridColumnStart: 5,
              gridColumnEnd: "span 8",
              marginBottom: theme.spacing(3),
            }}>
            <Button onClick={onClose}>Close</Button>
          </Box>
        </Box>
      </Container>
    </Drawer>
  );
}
